import {
  Box,
  FlexBox,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useMemo } from 'react';
import { matchPath, useLocation, useMatch } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import {
  CommandPaletteAction,
  CommandPaletteScope,
  useCommandPalette,
} from 'utils/commandPalette';
import { useCommands } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from '../../commandPalette.module.scss';
import { getCommandPaletteActionItems } from '../../utils/getCommandPaletteActionSelectionListItems';

/** Represents the main actions list of the command palette. */
export const MainSelectionList = (): JSX.Element => {
  const { pathname } = useLocation();
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { keybindings } = useCommands();
  // Match both /conversation/:csid and /conversation/:csid/activity-log
  const conversationRouteMatch = useMatch('/conversation/:csid/*');
  const isOnConversationPage = conversationRouteMatch !== null;
  const {
    actions: allActions,
    query,
    scope,
    isActive,
    createAction,
    performAction,
  } = useCommandPalette();

  const actions = useMemo(() => {
    const { all, current } = allActions
      .filter(
        (action) =>
          !action.scope || action.scope?.includes(CommandPaletteScope.Main),
      )
      .reduce<{
        all: readonly CommandPaletteAction[];
        current: readonly CommandPaletteAction[];
      }>(
        (actions, currentAction) => {
          if (currentAction.routes) {
            // Use matchPath to support parameterized routes like /conversation/:csid
            const matchesRoute = currentAction.routes.some((route) =>
              matchPath(route, pathname),
            );
            if (matchesRoute) {
              return {
                ...actions,
                current: [...actions.current, currentAction],
              };
            }
            return actions;
          }

          return { ...actions, all: [...actions.all, currentAction] };
        },
        { all: [], current: [] },
      );

    const combinedActions = [...current, ...all];

    // When on a conversation page, prioritize "Conversation" section actions
    if (isOnConversationPage) {
      return combinedActions.sort((a, b) => {
        const aIsConversation = a.section === 'Conversation';
        const bIsConversation = b.section === 'Conversation';
        if (aIsConversation && !bIsConversation) return -1;
        if (!aIsConversation && bIsConversation) return 1;
        return 0;
      });
    }

    return combinedActions;
  }, [allActions, pathname, isOnConversationPage]);

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandPaletteAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    const action = event.selectedItem.value;
    performAction(action);

    // Only close the modal if the action doesn't want to stay open
    if (!action.keepOpen) {
      dispatch(appActions.hideModal({ type: 'app:commandPalette' }));
    }
  };

  const { listActions, hasMatchingResults } = useMemo(() => {
    let results: CommandPaletteAction[] = [];
    let matchCount = 0;

    if (query) {
      const fuse = new Fuse(actions, {
        keys: ['label', 'aliases'],
        threshold: 0.2,
      });

      results = fuse.search(query).map(({ item }) => item);
      matchCount = results.length;

      // Add fallback search action when there's a query
      const fallbackSearchAction = createAction({
        label: `Search for "${query}"`,
        icon: 'search',
        section: 'Search',
        performFn: () =>
          navigate({
            path: '/search/:scope',
            params: {
              path: { scope: 'conversations' },
              search: { q: query },
            },
          }),
      });

      results.push(fallbackSearchAction);
    } else {
      results = [...actions];
      matchCount = results.length;
    }

    return { listActions: results, hasMatchingResults: matchCount > 0 };
  }, [actions, createAction, navigate, query]);

  if (scope !== CommandPaletteScope.Main) {
    return <></>;
  }

  // Show empty state when query exists but no commands match
  const showEmptyState = query && !hasMatchingResults;

  return (
    <>
      {showEmptyState && (
        <Box padding="300" className={styles.emptyState}>
          <FlexBox flexDirection="column" alignItems="center" gap="100">
            <span>No commands found for "{query}"</span>
            <span className={styles.emptyStateHint}>
              Press <kbd>↵</kbd> to search
            </span>
          </FlexBox>
        </Box>
      )}
      <SelectionList<CommandPaletteAction>
        isActive={isActive}
        onChange={handleSelectionListOnChange}
      >
        {getCommandPaletteActionItems(listActions, keybindings)}
      </SelectionList>
    </>
  );
};
